import React, { useState } from 'react';
import { Receipt, ImageOff, Maximize2 } from 'lucide-react';

/**
 * ETransferProofView — inline preview of a customer's e-transfer submission.
 * Used beside the QR code on the vendor + admin order views. Tapping the
 * thumbnail (single tap on desktop, double-tap on mobile) enlarges it in a
 * full-screen overlay.
 *
 * Props:
 *   - submission: { screenshot_url, reference, amount_cad, status,
 *                   invoice_kind, submitted_at }  (may be null / undefined)
 *   - amountExpected: number — used to render an in-line check that the
 *                     amount the customer entered matches what's on invoice.
 *   - orientation: 'row' | 'column' (default: 'column')
 */
export const ETransferProofView = ({ submission, amountExpected, orientation = 'column', testidPrefix = 'etransfer' }) => {
  const [enlarged, setEnlarged] = useState(false);
  const [lastTap, setLastTap] = useState(0);

  if (!submission) {
    return (
      <div className="rounded-lg border border-dashed border-slate-300 p-3 text-center text-[11px] text-slate-500" data-testid={`${testidPrefix}-empty`}>
        <Receipt className="h-4 w-4 mx-auto mb-1 text-slate-400" />
        No e-transfer proof yet
      </div>
    );
  }

  // Amount reconciliation: customer's submitted amount (from server) vs the
  // invoice amount the vendor / admin should be receiving.
  const submitted = Number(submission.amount_cad ?? amountExpected ?? 0);
  const expected = Number(amountExpected ?? submitted);
  const matches = Math.abs(submitted - expected) < 0.02;

  // Double-tap-to-enlarge on mobile, single-tap on desktop.
  const onTap = () => {
    const now = Date.now();
    // Mobile: expect two taps within 400ms.
    if (now - lastTap < 400) {
      setEnlarged(true);
      setLastTap(0);
      return;
    }
    setLastTap(now);
    // Desktop: also open immediately on a single click (matches user expectation).
    if (!('ontouchstart' in window)) setEnlarged(true);
  };

  return (
    <>
      <div
        className={`rounded-lg border ${matches ? 'border-emerald-200 bg-emerald-50/40' : 'border-amber-200 bg-amber-50/40'} p-2`}
        data-testid={`${testidPrefix}-proof`}
      >
        <div className="flex items-center justify-between gap-2 mb-1.5">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1">
            <Receipt className="h-3 w-3" /> E-transfer proof
          </div>
          <span
            className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${matches ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}
            data-testid={`${testidPrefix}-match-badge`}
          >
            {matches ? 'Amount matches' : 'Amount differs'}
          </span>
        </div>
        <div className="text-[11px] text-slate-700 flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <span>CA${submitted.toFixed(2)}</span>
          {!matches && (
            <span className="text-amber-700">/ invoice CA${expected.toFixed(2)}</span>
          )}
          {submission.reference && (
            <>
              <span className="text-slate-300">·</span>
              <code className="text-[10px] bg-white px-1 py-0.5 rounded border border-slate-200">
                Ref {submission.reference}
              </code>
            </>
          )}
        </div>
        {submission.screenshot_url ? (
          <button
            type="button"
            onClick={onTap}
            className={`relative mt-2 block w-full group ${orientation === 'row' ? 'aspect-video' : ''}`}
            data-testid={`${testidPrefix}-thumb`}
          >
            <img
              src={submission.screenshot_url}
              alt="E-transfer confirmation"
              className="w-full max-h-40 object-contain rounded bg-white border border-slate-200"
            />
            <span className="absolute top-1 right-1 bg-white/90 rounded-full px-1.5 py-0.5 text-[10px] text-slate-700 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition">
              <Maximize2 className="h-3 w-3" /> Tap twice to enlarge
            </span>
          </button>
        ) : (
          <div className="mt-2 flex items-center justify-center gap-1 text-[11px] text-slate-400 py-3">
            <ImageOff className="h-3.5 w-3.5" /> Screenshot missing or expired
          </div>
        )}
        <div className="mt-1 text-[10px] text-slate-500">Status · <span className="capitalize">{submission.status || 'pending'}</span></div>
      </div>

      {enlarged && submission.screenshot_url && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setEnlarged(false)}
          role="button"
          data-testid={`${testidPrefix}-enlarged`}
        >
          <img
            src={submission.screenshot_url}
            alt="E-transfer confirmation full-size"
            className="max-w-full max-h-full object-contain rounded shadow-2xl cursor-zoom-out"
          />
        </div>
      )}
    </>
  );
};

export default ETransferProofView;
